import {
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
} from "@tanstack/react-router";
import { lazy } from "react";
import { Role, type UserInfoDto, useUserInfo } from "../api/custom";
import { MunLoading } from "./components/design-system/MunLoading";
import { AppLayout } from "./components/layout/AppLayout";
import { OrderHistoryPage } from "./pages/sales/history/OrderHistoryPage";

export function doesRoleExist(userinfo: UserInfoDto, role: Role) {
  return userinfo.role === role;
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "auth-layout",
  component: AuthLayout,
});

function AuthLayout() {
  const { data: userInfo, isLoading, error } = useUserInfo();

  if (isLoading) {
    return <MunLoading />;
  }

  if (error) {
    return <Navigate to="/login" />;
  }

  if (!userInfo) {
    return <Navigate to="/login" />;
  }

  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}

function MunchiAdminLayout() {
  const { data, isLoading, error } = useUserInfo();

  if (isLoading) {
    return <MunLoading />;
  }

  if (error) {
    return <Navigate to="/login" />;
  }

  if (!data || !doesRoleExist(data.userInfo, Role.MUNCHI_ADMIN)) {
    return <Navigate to="/" />;
  }

  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}

// Dashboard
const WelcomePage = lazy(() => import("./pages/welcome/WelcomePage"));
const TotalSalesPage = lazy(() => import("./pages/dashboard/TotalSales"));
const TotalOrdersPage = lazy(() => import("./pages/dashboard/TotalOrders"));
const SalesOverviewPage = lazy(() => import("./pages/dashboard/SalesOverview"));

// Analytics
const SalesPressurePage = lazy(() => import("./pages/analytics/SalesPressure"));
const StatusBreakdownPage = lazy(
  () => import("./pages/analytics/StatusBreakdown")
);
const ProductPerformancePage = lazy(
  () => import("./pages/analytics/ProductPerformance")
);

// Sales
const LatestSalesPage = lazy(() => import("./pages/sales/LatestSales"));
const POSSalesPage = lazy(() => import("./pages/sales/POSSales"));
const MunchiSalesPage = lazy(() => import("./pages/sales/MunchiSales"));
const TaxMunchiPage = lazy(() => import("./pages/sales/TaxMunchi"));
const TaxPosPage = lazy(() => import("./pages/sales/TaxPos"));

// Reports
const SalesReportPage = lazy(() => import("./pages/reports/SalesReport"));
const MunchiOnlineSalesReportPage = lazy(
  () => import("./pages/reports/MunchiOnlineSalesReport")
);

// Venues
const InventoryPage = lazy(() => import("./pages/venues/Inventory"));

// Customers
const CompanyPage = lazy(() => import("./pages/customers/company/Company"));
const InvoiceOrderHistoryPage = lazy(
  () => import("./pages/customers/company/InvoiceOrderHistoryPage")
);
const CustomerLoyaltyPage = lazy(
  () => import("./pages/customers/customer/CustomerLoyalty")
);

// Users
const ActivityPage = lazy(() => import("./pages/users/Activity"));
const UsersListPage = lazy(() => import("./pages/users/list/UsersList"));
const RolesPage = lazy(() => import("./pages/users/Roles"));
const ShiftsPage = lazy(() => import("./pages/users/Shifts"));

// Management
const KioskPage = lazy(() => import("./pages/management/kiosk/KioskPage"));
const KioskManagementPage = lazy(
  () => import("./pages/management/kiosk/Kiosk")
);
const KioskCreateOnboardPage = lazy(
  () => import("./pages/management/kiosk/KioskCreateOnboard")
);
const KioskThemePage = lazy(() => import("./pages/management/kiosk/theme/KioskTheme"));
const ManagementPage = lazy(() => import("./pages/management/index"));
const PaymentRatePage = lazy(() => import("./pages/operations/payment-rate/PaymentRate"));

const homeRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/dashboard",
  component: TotalSalesPage,
});

const welcomeRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/",
  component: WelcomePage,
});

const totalOrdersRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/orders",
  component: TotalOrdersPage,
});

const salesOverviewRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/sales-overview",
  component: SalesOverviewPage,
});

const productPerformanceRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/product-performance",
  component: ProductPerformancePage,
});

const salesPressureRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/pressure",
  component: SalesPressurePage,
});

const statusBreakdownRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/breakdown",
  component: StatusBreakdownPage,
});

const latestSalesRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/latest-sales",
  component: LatestSalesPage,
});

const posSalesRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/pos-sales",
  component: POSSalesPage,
});

const munchiSalesRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/munchi-sales",
  component: MunchiSalesPage,
});

const posTaxRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/pos-tax",
  component: TaxPosPage,
});

const munchiTaxRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/munchi-tax",
  component: TaxMunchiPage,
});

const salesReportRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/reports/sales",
  component: SalesReportPage,
});

const munchiOnlineSalesReportRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/reports/munchi-online",
  component: MunchiOnlineSalesReportPage,
});

const inventoryRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/venues/inventory",
  component: InventoryPage,
});

const companyRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/customers/company",
  component: CompanyPage,
});

const invoiceOrderHistoryRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/customers/company/$businessId/history/$invoiceCompanyId",
  component: InvoiceOrderHistoryPage,
});

const customerLoyaltyRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/customers/analytics",
  component: CustomerLoyaltyPage,
});

const usersListRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/users/list",
  component: UsersListPage,
});

const activityRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/users/activity",
  component: ActivityPage,
});

const rolesRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/users/roles",
  component: RolesPage,
});

const shiftsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/users/shifts",
  component: ShiftsPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: lazy(() => import("./pages/auth/Login")),
});

const postloginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/postlogin",
  component: lazy(() => import("./pages/auth/Postlogin")),
});

const orderHistoryRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/orders/history",
  component: OrderHistoryPage,
});

const managementRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/management",
  component: ManagementPage,
});

const kioskPageRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/management/kiosks",
  component: KioskPage,
});

const kioskManagementRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/management/kiosks/devices",
  component: KioskManagementPage,
});

const kioskCreateOnboardRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/management/kiosks/create",
  component: KioskCreateOnboardPage,
});

const kioskThemeRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/management/kiosks/theme",
  component: KioskThemePage,
});

const munchiAdminLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "munchi-admin-layout",
  component: MunchiAdminLayout,
});

const paymentRateRoute = createRoute({
  getParentRoute: () => munchiAdminLayoutRoute,
  path: "/operations/payment-rate",
  component: PaymentRatePage,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  postloginRoute,
  authLayoutRoute.addChildren([
    welcomeRoute,
    homeRoute,
    totalOrdersRoute,
    salesOverviewRoute,
    productPerformanceRoute,
    salesPressureRoute,
    statusBreakdownRoute,
    latestSalesRoute,
    posSalesRoute,
    munchiSalesRoute,
    posTaxRoute,
    munchiTaxRoute,
    salesReportRoute,
    munchiOnlineSalesReportRoute,
    inventoryRoute,
    invoiceOrderHistoryRoute,
    companyRoute,
    customerLoyaltyRoute,
    activityRoute,
    usersListRoute,
    rolesRoute,
    shiftsRoute,
    orderHistoryRoute,
    kioskPageRoute,
    kioskManagementRoute,
    kioskCreateOnboardRoute,
    kioskThemeRoute,
    managementRoute,
  ]),
  munchiAdminLayoutRoute.addChildren([
    paymentRateRoute,
  ]),
]);
